import { AxiosResponse } from 'axios';
import { CreateSpecification } from './digitaltwin';
import Archivable, { ArchivedUpdatedEvent } from './shared/archivable';
import Draftable from './shared/draftable';
import Entity from './shared/entity';
import { Event } from './shared/eventstore-client/EventstoreClient';
import { ServiceClientWithEventstore } from './shared/service-client/ServiceClientWithEventstore';

export interface MessageResponse {
  message: string;
}

export interface Template extends TemplateProperties, Entity, Archivable, Draftable {}
export interface TemplateBatch extends TemplateBatchProperties, Entity {}

export interface TemplateProperties {
  tags?: string[];
  specification: CreateSpecification;
}

export type TemplateBatchSpecification = Omit<CreateSpecification, 'image' | 'presentationId'> & {
  imageUrl?: string;
  presentationId?: string;
};
export interface TemplateBatchProperties {
  entries: TemplateBatchEntry[];
  status: BatchStatus;
  statusMessage?: string;
}

export type CreateTemplateInput = TemplateProperties & Draftable;
export type UpdateTemplateInput = Pick<TemplateProperties, 'tags'> & {
  specification?: Partial<CreateSpecification>;
} & Draftable;

export type TemplateBatchEntry = Omit<CreateTemplateInput, 'specification'> & {
  specification: TemplateBatchSpecification;
  entryId: string;
  status: EntryStatus;
  statusMessage?: string;
};

export type CreateTemplateBatchEntryInput = Omit<CreateTemplateInput, 'specification'> & {
  specification: TemplateBatchSpecification;
};

export enum EntryStatus {
  SUCCESS = 'SUCCESS',
  PENDING = 'PENDING',
  FAILED = 'FAILED',
}
export enum BatchStatus {
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}
export interface CreateTemplateBatchInput {
  entries: CreateTemplateBatchEntryInput[];
}

export type PERMISSIONS =
  | typeof DELETE
  | typeof GET_ALL
  | typeof GET
  | typeof GET_BATCH
  | typeof GET_ALL_BATCHES
  | typeof CREATE
  | typeof CREATE_BATCH
  | typeof UPDATE
  | typeof SET_ARCHIVED
  | typeof GET_EVENTS_ANY
  | typeof GET_EVENTS_CREATED
  | typeof GET_EVENTS_UPDATED
  | typeof GET_EVENTS_DELETED
  | typeof GET_EVENTS_ARCHIVED_UPDATED;

export const CREATE = 'TEMPLATE:CREATE';
export const CREATE_BATCH = 'TEMPLATE:CREATE_BATCH';
export const UPDATE = 'TEMPLATE:UPDATE';
export const DELETE = 'TEMPLATE:DELETE';
export const GET = 'TEMPLATE:GET';
export const GET_BATCH = 'TEMPLATE:GET_BATCH';
export const GET_ALL = 'TEMPLATE:GET_ALL';
export const GET_ALL_BATCHES = 'TEMPLATE:GET_ALL_BATCHES';
export const SET_ARCHIVED = 'TEMPLATE:SET_ARCHIVED';
export const GET_EVENTS_ANY = 'TEMPLATE:GET_EVENTS:ANY';
export const GET_EVENTS_CREATED = 'TEMPLATE:GET_EVENTS:CREATED';
export const GET_EVENTS_DELETED = 'TEMPLATE:GET_EVENTS:DELETED';
export const GET_EVENTS_UPDATED = 'TEMPLATE:GET_EVENTS:UPDATED';
export const GET_EVENTS_ARCHIVED_UPDATED = 'TEMPLATE:GET_EVENTS:ARCHIVED_UPDATED';

export const PERMISSION_NAMES = {
  CREATE,
  CREATE_BATCH,
  UPDATE,
  DELETE,
  GET,
  GET_ALL,
  GET_BATCH,
  GET_ALL_BATCHES,
  SET_ARCHIVED,
  GET_EVENTS_ANY,
  GET_EVENTS_CREATED,
  GET_EVENTS_DELETED,
  GET_EVENTS_UPDATED,
  GET_EVENTS_ARCHIVED_UPDATED,
};

export interface CreatedEvent extends Event {
  type: 'CREATED';
  data: Template;
}

export interface UpdatedEvent extends Event {
  type: 'UPDATED';
  data: UpdateTemplateInput;
}

export interface DeletedEvent extends Event {
  type: 'DELETED';
}

export type TemplateEvents = CreatedEvent | DeletedEvent | UpdatedEvent | ArchivedUpdatedEvent;

export interface GetTemplatesResponse {
  templates: Template[];
  continuationToken?: string;
}

export interface GetTemplatesRequest {
  limit?: number;
  continuationToken?: string;
  allowArchived?: boolean;
  allowDraft?: boolean;
}

export class TemplateClient extends ServiceClientWithEventstore<TemplateEvents> {
  public async get(id: string): Promise<AxiosResponse<Template>> {
    return await this.httpClient.get(`/${id}`, this.requestConfig);
  }

  public async getAll(input: GetTemplatesRequest = {}): Promise<AxiosResponse<GetTemplatesResponse>> {
    return await this.httpClient.get('/', {
      params: input,
      ...this.requestConfig,
    });
  }

  public async create(t: CreateTemplateInput): Promise<AxiosResponse<Template>> {
    return await this.httpClient.post('/', t, this.requestConfig);
  }

  public async update(id: string, t: UpdateTemplateInput): Promise<AxiosResponse<Template>> {
    return await this.httpClient.patch(`/${id}`, t, this.requestConfig);
  }

  public async delete(id: string): Promise<AxiosResponse<MessageResponse>> {
    return await this.httpClient.delete(`/${id}`, this.requestConfig);
  }

  public async setArchived(id: string, archived: boolean): Promise<AxiosResponse<Template>> {
    return await this.httpClient.put(`/${id}/archived`, { archived }, this.requestConfig);
  }

  public async createBatch(t: CreateTemplateBatchInput): Promise<AxiosResponse<TemplateBatch>> {
    return await this.httpClient.post('/batch', t, this.requestConfig);
  }

  public async validateCreateBatch(t: CreateTemplateBatchInput): Promise<AxiosResponse<{ message: string }>> {
    return await this.httpClient.post('/batch/validate', t, this.requestConfig);
  }

  public async getBatch(id: string): Promise<AxiosResponse<TemplateBatch>> {
    return await this.httpClient.get(`/batch/${id}`, this.requestConfig);
  }
}
